/**
 * frontend/src/components/ExportacionBeneficios/ExportacionBeneficios.jsx
 *
 * Selecciona el tipo de entrega (1, 2 o 3), consulta el roster de titulares
 * pendientes (control_entregas_3) con filtros opcionales, y descarga el
 * Excel oficial: el backend diligencia la PLANTILLA REAL de esa entrega
 * (misma hoja, mismos títulos, mismas columnas), no genera un libro nuevo.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Select,
  Spin,
  Empty,
  Statistic,
  Space,
  Tooltip,
  message,
} from 'antd';
import {
  DownloadOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { saveAs } from 'file-saver';
import * as ExportacionBeneficiosService from '../../services/exportacionBeneficios/exportacionBeneficiosService';

const { Option } = Select;

const ENTREGAS = [
  { label: 'ENTREGA 1 - HAMBRE CERO 2025', value: 1 },
  { label: 'ENTREGA 2 - HAMBRE CERO 2025', value: 2 },
  { label: 'ENTREGA 3 - HAMBRE CERO 2025', value: 3 },
];

/** El backend devuelve el error como JSON dentro de un Blob cuando responseType='blob'. */
async function extraerMensajeError(error) {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const texto = await data.text();
      const json = JSON.parse(texto);
      return json.mensaje || 'Error inesperado';
    } catch {
      return 'Error inesperado';
    }
  }
  return data?.mensaje || error?.message || 'Error inesperado';
}

export default function ExportacionBeneficios() {
  const [form] = Form.useForm();

  const [numeroEntrega, setNumeroEntrega] = useState(1);
  const [municipios, setMunicipios] = useState([]);
  const [usuariosRit, setUsuariosRit] = useState([]);

  const [datosExportacion, setDatosExportacion] = useState(null);
  const [sujetos, setSujetos] = useState([]);
  const [recolectores, setRecolectores] = useState([]);

  const [cargandoDatos, setCargandoDatos] = useState(false);
  const [cargandoCatalogos, setCargandoCatalogos] = useState(true);
  const [generandoExcel, setGenerandoExcel] = useState(false);
  const [mostrarRecolectores, setMostrarRecolectores] = useState(false);
  const [paginacion, setPaginacion] = useState({ current: 1, pageSize: 10 });

  const cargarCatalogos = useCallback(async (entrega, municipioOid) => {
    try {
      setCargandoCatalogos(true);
      const respuesta = await ExportacionBeneficiosService.obtenerCatalogos(entrega, municipioOid);

      if (respuesta.exito) {
        setMunicipios(respuesta.datos?.municipios || []);
        setUsuariosRit(respuesta.datos?.usuariosRit || []);
      } else {
        message.error(respuesta.mensaje || 'Error cargando catálogos');
      }
    } catch (error) {
      message.error('Error cargando catálogos');
    } finally {
      setCargandoCatalogos(false);
    }
  }, []);

  useEffect(() => {
    cargarCatalogos(numeroEntrega, undefined);
  }, [numeroEntrega, cargarCatalogos]);

  const handleCambiarEntrega = (valor) => {
    setNumeroEntrega(valor);
    setSujetos([]);
    setRecolectores([]);
    setDatosExportacion(null);
    form.resetFields();
  };

  const handleCambiarMunicipio = (valor) => {
    form.setFieldValue('usuarioRIT', undefined);
    cargarCatalogos(numeroEntrega, valor);
  };

  const handleAplicarFiltros = async (values) => {
    try {
      setCargandoDatos(true);
      const filtros = {
        numeroEntrega,
        municipio_oid: values.municipio_oid,
        usuarioRIT: values.usuarioRIT,
      };

      const respuesta = await ExportacionBeneficiosService.obtenerDatos(filtros);

      if (respuesta.exito && respuesta.datos) {
        setSujetos(respuesta.datos.sujetos || []);
        setRecolectores(respuesta.datos.recolectores || []);
        setDatosExportacion(respuesta.datos);
        setMostrarRecolectores(false);
        setPaginacion({ current: 1, pageSize: 10 });
        message.success(`${respuesta.datos.sujetos.length} titulares pendientes encontrados`);
      } else {
        message.error(respuesta.mensaje || 'Error obteniendo datos');
        setDatosExportacion(null);
        setSujetos([]);
        setRecolectores([]);
      }
    } catch (error) {
      message.error('Error obteniendo datos');
    } finally {
      setCargandoDatos(false);
    }
  };

  const handleLimpiarFiltros = () => {
    form.resetFields();
    setSujetos([]);
    setRecolectores([]);
    setDatosExportacion(null);
    setMostrarRecolectores(false);
    cargarCatalogos(numeroEntrega, undefined);
  };

  const generarExcel = async () => {
    if (!datosExportacion || sujetos.length === 0) {
      message.warning('No hay datos para exportar. Aplica los filtros primero.');
      return;
    }

    try {
      setGenerandoExcel(true);
      const values = form.getFieldsValue();
      const { blob, nombreArchivo } = await ExportacionBeneficiosService.descargarExcel({
        numeroEntrega,
        municipio_oid: values.municipio_oid,
        usuarioRIT: values.usuarioRIT,
      });

      saveAs(blob, nombreArchivo);
      message.success('Archivo generado exitosamente');
    } catch (error) {
      message.error(await extraerMensajeError(error));
    } finally {
      setGenerandoExcel(false);
    }
  };

  const obtenerColumnasTabla = () => {
    const origen = mostrarRecolectores ? recolectores : sujetos;
    if (origen.length === 0) return [];

    return Object.keys(origen[0]).map((clave) => ({
      title: clave,
      dataIndex: clave,
      key: clave,
      width: 150,
      ellipsis: { showTitle: false },
      render: (texto) => (
        <Tooltip title={texto}>
          {texto === '' || texto === null || texto === undefined ? '-' : String(texto)}
        </Tooltip>
      ),
    }));
  };

  const columnas = obtenerColumnasTabla();

  return (
    <div style={{ padding: '20px' }}>
      <Card
        title={<h2>📊 Exportación de Beneficios</h2>}
        extra={
          <Button icon={<ReloadOutlined />} onClick={handleLimpiarFiltros}>
            Limpiar
          </Button>
        }
        style={{ marginBottom: '20px' }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Entrega">
              <Select value={numeroEntrega} onChange={handleCambiarEntrega}>
                {ENTREGAS.map((e) => (
                  <Option key={e.value} value={e.value}>
                    {e.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card title="🔍 Filtros" style={{ marginBottom: '20px' }}>
        <Spin spinning={cargandoCatalogos}>
          <Form form={form} layout="vertical" onFinish={handleAplicarFiltros}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="municipio_oid" label="Municipio">
                  <Select
                    placeholder="Todos los municipios del cierre"
                    allowClear
                    loading={cargandoCatalogos}
                    onChange={handleCambiarMunicipio}
                    showSearch
                    optionFilterProp="children"
                  >
                    {municipios.map((m) => (
                      <Option key={m.municipio_oid} value={m.municipio_oid}>
                        {m.municipio}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <Form.Item name="usuarioRIT" label="Usuario RIT (Recolector)">
                  <Select
                    placeholder="Todos"
                    allowClear
                    loading={cargandoCatalogos}
                    showSearch
                    optionFilterProp="children"
                  >
                    {usuariosRit.map((u) => (
                      <Option key={u.usuarioRIT} value={u.usuarioRIT}>
                        {[u.nombres_encuestador, u.apellidos_encuestador].filter(Boolean).join(' ') || u.usuarioRIT}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8} style={{ display: 'flex', alignItems: 'flex-end' }}>
                <Form.Item style={{ width: '100%', marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SearchOutlined />}
                    loading={cargandoDatos}
                    block
                  >
                    Consultar
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Spin>
      </Card>

      {datosExportacion && (
        <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Titulares pendientes"
                value={datosExportacion.resumen.total_sujetos}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Recolectores (RIT)"
                value={datosExportacion.resumen.total_recolectores}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={12}>
            <Card>
              <Space>
                <Button
                  type="primary"
                  size="large"
                  icon={<DownloadOutlined />}
                  loading={generandoExcel}
                  onClick={generarExcel}
                >
                  Descargar Excel
                </Button>
                <Button onClick={() => setMostrarRecolectores(!mostrarRecolectores)}>
                  {mostrarRecolectores ? 'Ver Titulares' : 'Ver Recolectores'}
                </Button>
              </Space>
            </Card>
          </Col>
        </Row>
      )}

      {datosExportacion && (
        <Card
          title={mostrarRecolectores ? '📋 Recolectores' : '👥 Titulares pendientes'}
          style={{ marginBottom: '20px' }}
        >
          {(mostrarRecolectores ? recolectores : sujetos).length === 0 ? (
            <Empty description="Sin datos" />
          ) : (
            <Table
              columns={columnas}
              dataSource={mostrarRecolectores ? recolectores : sujetos}
              rowKey={(_, idx) => idx}
              pagination={paginacion}
              onChange={(pag) => setPaginacion(pag)}
              size="small"
              scroll={{ x: 1200 }}
              locale={{ emptyText: 'Sin datos' }}
            />
          )}
        </Card>
      )}

      {!datosExportacion && !cargandoDatos && (
        <Card>
          <Empty
            description="Selecciona la entrega, aplica filtros (opcionales) y haz clic en 'Consultar'"
            style={{ marginTop: '50px' }}
          />
        </Card>
      )}
    </div>
  );
}
